/**
 * Home "Resumen Rápido" counters.
 *
 * They used state strings that don't exist ('pendiente', 'en revisión',
 * 'propuesta', 'en curso', 'ejecución'), so "Ofertas Pendientes" was always 0,
 * "Oportunidades Activas" counted lost offers and drafts alike, and "Proyectos
 * en Curso" counted won offers (completed projects included). They now use the
 * real snake_case states, and the two figures Estadísticas also shows come from
 * the same GET /dashboard/stats so both pages agree.
 */

/** Offer states still being worked on (the terminal ones are ganada/perdida/cancelada). */
export const OPEN_OFFER_STATES = ['borrador', 'en_preparacion', 'en_revision', 'presentada', 'en_negociacion']
/** Open offers already under preparation or waiting for the client (drafts excluded). */
export const PENDING_OFFER_STATES = ['en_preparacion', 'en_revision', 'presentada', 'en_negociacion']
/** Project states that are not finished: the modal's values plus the enum registry's legacy ones.
 *  Same list as the dashboard's sector breakdown (dashboard.py). */
export const LIVE_PROJECT_STATES = [
    'no_iniciado', 'en_curso', 'en_espera', 'pendiente', 'planificacion', 'pausado', 'mantenimiento',
]

const PRACTICAS_NAME = 'desarrollo y formación'

export interface HomeStats {
    activeOpportunities: number
    teamMembers: number
    activeProjects: number
    pendingOffers: number
}

const lower = (v: unknown): string => (typeof v === 'string' ? v.trim().toLowerCase() : '')

const ofertaEstado = (o: any): string => lower(o?.oferta?.estado ?? o?.oferta_estado)
const proyectoEstado = (o: any): string => lower(o?.proyecto?.estado ?? o?.proyecto_estado)
const isPracticas = (o: any): boolean => lower(o?.general?.name ?? o?.name) === PRACTICAS_NAME

/**
 * @param opportunities GET /opportunities
 * @param dashboardStats GET /dashboard/stats, or null when it failed
 * @param users GET /users, only used as a fallback when the stats failed
 */
export const computeHomeStats = (opportunities: any[], dashboardStats: any | null, users: any[] = []): HomeStats => {
    // The interns' synthetic slot is not a real opportunity (the dashboard excludes it too).
    const real = opportunities.filter((o) => !isPracticas(o))

    // Active = project not finished, or no project yet and the offer is still open
    // (the dashboard's sector-breakdown rule).
    const activeOpportunities = real.filter((o) => {
        const proyecto = proyectoEstado(o)
        if (proyecto) return LIVE_PROJECT_STATES.includes(proyecto)
        return OPEN_OFFER_STATES.includes(ofertaEstado(o))
    }).length

    const pendingOffers = real.filter((o) => PENDING_OFFER_STATES.includes(ofertaEstado(o))).length

    // Estadísticas' "Activas" (proyecto_estado = en_curso) and "Equipo" (no admin, no interns).
    const summary = dashboardStats?.summary
    const activeProjects = typeof summary?.active_opportunities === 'number'
        ? summary.active_opportunities
        : real.filter((o) => proyectoEstado(o) === 'en_curso').length
    const teamMembers = typeof summary?.total_users === 'number'
        ? summary.total_users
        : users.filter((u) => lower(u?.username) !== 'admin' && lower(u?.user_type) !== 'intern').length

    return { activeOpportunities, teamMembers, activeProjects, pendingOffers }
}
